// 主人公の致命の新しい版の光 (2026-10-12・開発版 ?lunaFinisher=new だけ・決まり = src/luna_finisher.ts)。
//   守る好み: 白い効果線は描かない (線にせず火花のように散らす)・白で画面を隠さない・赤は使わない (弾けない攻撃の専用色)。
//   色 = 青白 (SOUL_BLUE・ほどける光) と 金の火の粉 (灯火)。全部やわらかい光の粒と面で描く。
//   描画だけ (ゲームの状態は変えない)。世界座標の ctx (render.ts の世界の層) へ描く。
import { ARENA, SOUL_BLUE } from './balance'
import type { FinisherProfile } from './luna_finisher'
import { finisherBgDark, finisherFreezeProgress, finisherPillar } from './luna_finisher'

function h01(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha: number): void {
  if (alpha <= 0.01 || r <= 0.4) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, color)
  g.addColorStop(0.35, color)
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

/**
 * 飛沫 (青白い光の粒 + 金の火の粉)。x,y = 刺し傷・awayDir = 抜く向き (主人公の側 = 主人公の向きの逆)・t = 抜いてからの秒。
 *   青白い粒: 抜く向きへ扇形に飛び、重さで落ちながら消える。火の粉: 遅く飛んで上へ漂い、ちらついて消える。
 */
export function drawPullSpray(ctx: CanvasRenderingContext2D, x: number, y: number, awayDir: 1 | -1, t: number,
  nBlue: number, nEmber: number, seed: number, scale = 1): void {
  if (t < 0) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < nBlue; i++) {
    const life = 0.45 + h01(seed + i * 3.1) * 0.4
    const lt = t / life
    if (lt >= 1) continue
    // 角度: 水平 (抜く向き) から上へ 70° まで・下へ 15° まで。上寄りに多く。
    const ang = (-0.26 + h01(seed + i * 1.7) ** 0.8 * 1.48)
    const sp = (170 + h01(seed + i * 2.3) * 280) * scale
    const drag = 1 - Math.exp(-t * 3.2)
    const dist = sp * drag / 3.2
    const px = x + awayDir * Math.cos(ang) * dist
    const py = y - Math.sin(ang) * dist + 520 * t * t * 0.5
    const r = (1.6 + h01(seed + i * 5.9) * 2.2) * (1 - 0.4 * lt) * scale
    const a = (1 - lt) * (1 - lt) * (0.75 + h01(seed + i * 7.3) * 0.25)
    glow(ctx, px, py, r * 2.6, SOUL_BLUE.head, a * 0.45)
    glow(ctx, px, py, r, SOUL_BLUE.white, a)
  }
  for (let i = 0; i < nEmber; i++) {
    const delay = h01(seed + i * 9.7) * 0.06
    const tt = t - delay
    if (tt < 0) continue
    const life = 0.6 + h01(seed + i * 4.1) * 0.45
    const lt = tt / life
    if (lt >= 1) continue
    const ang = (0.05 + h01(seed + i * 6.3) * 1.35)
    const sp = (90 + h01(seed + i * 8.1) * 200) * scale
    const drag = 1 - Math.exp(-tt * 2.4)
    const dist = sp * drag / 2.4
    const sway = Math.sin(tt * (6 + h01(seed + i) * 5) + i) * 4
    const px = x + awayDir * Math.cos(ang) * dist + sway
    const py = y - Math.sin(ang) * dist - 30 * tt + 60 * tt * tt
    const flick = 0.65 + 0.35 * Math.sin(tt * 38 + i * 2.1)
    const a = (1 - lt) * flick
    const r = (1.2 + h01(seed + i * 2.9) * 1.4) * scale
    glow(ctx, px, py, r * 3, 'rgba(244,190,96,1)', a * 0.35)
    glow(ctx, px, py, r, '#ffe7a8', a)
  }
  ctx.restore()
}

/** 普段の致命の各発の光 (luna_exec)。age = その発からの秒 (止めの間は進まない)。 */
export function drawLunaExecHit(ctx: CanvasRenderingContext2D, f: { hit: 1 | 2 | 3, x: number, y: number, dir: 1 | -1 }, age: number): void {
  const away = (-f.dir) as 1 | -1
  // 止めの間 (age 0) でも粒が少し外へ出ている姿で見せる (生まれた瞬間の点の重なりにしない)
  const t = age + 0.03
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (f.hit === 1) {
    // 刺す: 刺し傷の小さな灯 + 手前へはじける小さな粒
    const k = Math.max(0, 1 - age / 0.2)
    glow(ctx, f.x, f.y, 8 + 6 * (1 - k), SOUL_BLUE.head, k * 0.3)
    glow(ctx, f.x, f.y, 3.5, SOUL_BLUE.white, k * 0.8)
    ctx.restore()
    drawPullSpray(ctx, f.x, f.y, away, t, 9, 3, 11, 0.55)
    return
  }
  if (f.hit === 2) {
    // 押し込む: 傷の光が奥で脈打つ (内から漏れる) + 上へ漂う火の粉
    const k = Math.max(0, 1 - age / 0.35)
    const pulse = Math.sin(Math.min(1, age / 0.35) * Math.PI)
    glow(ctx, f.x, f.y, 10 + 8 * pulse, SOUL_BLUE.head, (0.18 + 0.2 * pulse) * k)
    glow(ctx, f.x, f.y, 4, SOUL_BLUE.white, 0.7 * k)
    ctx.restore()
    drawPullSpray(ctx, f.x, f.y, away, t, 5, 8, 23, 0.5)
    return
  }
  // 抜く: 傷から抜く向きへ青白い飛沫と火の粉。芯の光は短く強く、薄い靄が手前へ流れて消える。
  const k = Math.max(0, 1 - age / 0.28)
  glow(ctx, f.x, f.y, 13 + 8 * (1 - k), SOUL_BLUE.head, k * 0.32)
  glow(ctx, f.x, f.y, 4.5, SOUL_BLUE.white, k * 0.9)
  const hz = Math.max(0, 1 - age / 0.8)
  glow(ctx, f.x + away * (14 + age * 70), f.y - 6 - age * 18, 44, SOUL_BLUE.hazeLight, hz * hz * 0.16)
  ctx.restore()
  drawPullSpray(ctx, f.x, f.y, away, t, 24, 14, 37, 1)
}

/** 最後の一撃: 止めの間、背景 (床まで) だけ暗く沈める。キャラより前に描く (render.ts が床の直後に呼ぶ)。 */
export function drawFinisherV2BgDark(ctx: CanvasRenderingContext2D, elapsedMs: number, hitStopRemainingSec: number,
  profile: FinisherProfile, screenX: number): void {
  const a = finisherBgDark(elapsedMs, hitStopRemainingSec, profile)
  if (a <= 0.005) return
  ctx.save()
  ctx.globalAlpha = a
  ctx.fillStyle = 'rgb(5,7,16)'
  ctx.fillRect(screenX - 40, -40, ARENA.width + 80, ARENA.height + 80)
  ctx.restore()
}

/** 背景 (床まで) を濃さ a の黒で沈める (最後の一撃 C の暗さ = src/luna_finisher.ts finisherCBgDark)。 */
export function drawBgDarkAlpha(ctx: CanvasRenderingContext2D, a: number, screenX: number): void {
  if (a <= 0.005) return
  ctx.save()
  ctx.globalAlpha = a
  ctx.fillStyle = 'rgb(5,7,16)'
  ctx.fillRect(screenX - 40, -40, ARENA.width + 80, ARENA.height + 80)
  ctx.restore()
}

/**
 * 最後の一撃: 刺し傷の灯 (止めの間) → 抜いた瞬間の飛沫 → 光の柱 (傷から上へ)。キャラの前・HUD の下に描く。
 *   wound = 刺し傷・dir = 主人公の向き。
 */
export function drawFinisherV2Front(ctx: CanvasRenderingContext2D, elapsedMs: number, hitStopRemainingSec: number,
  profile: FinisherProfile, wound: { x: number, y: number, dir: 1 | -1 }, floorY: number): void {
  const fp = finisherFreezeProgress(elapsedMs, hitStopRemainingSec, profile)
  const away = (-wound.dir) as 1 | -1
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 刺し傷の灯: 止めの間は小さく息づく (画面を白くしない・形を隠さない)。抜いた後は短く消える。
  {
    let a = 0
    let r = 10
    if (fp !== null) {
      const inK = Math.min(1, fp * profile.freezeMs / 60)
      a = inK * (0.6 + 0.12 * Math.sin(fp * Math.PI * 3))
      r = 5 + 2 * fp
    } else {
      const t = elapsedMs - profile.pullAtMs
      a = t < 0 ? 0.65 : Math.max(0, 0.75 * (1 - t / 220))
      r = t < 0 ? 7 : 7 + t * 0.03
    }
    glow(ctx, wound.x, wound.y, r * 2.6, SOUL_BLUE.head, a * 0.25)
    glow(ctx, wound.x, wound.y, r, SOUL_BLUE.white, a)
  }
  // 光の柱: 傷から天井へ立つ。やわらかい面 (中心が白く・縁は青白く消える)。縦の線は描かない。
  const pil = finisherPillar(elapsedMs, profile)
  if (pil.level > 0.005) {
    const top = -20
    const height = (wound.y - top) * pil.rise
    const w = profile.pillarHalfWidth * (0.7 + 0.3 * pil.rise)
    // 段は整数の行で区切り、重ねも隙間も作らない (重なりの 0.5px が横縞に見えた・10-12 撮影)。
    const SEG = Math.max(1, Math.min(90, Math.round(height / 4)))
    for (let s = 0; s < SEG; s++) {
      const y0 = Math.round(wound.y - (height * s) / SEG)
      const y1 = Math.round(wound.y - (height * (s + 1)) / SEG)
      if (y0 <= y1) continue
      const u = (s + 0.5) / SEG
      // 上へ行くほど薄く (先端は溶ける)・根元は少し太い
      const along = (1 - u) ** 0.6 * (u < 0.06 ? u / 0.06 : 1)
      const ww = w * (1.25 - 0.45 * u)
      const g = ctx.createLinearGradient(wound.x - ww * 3, 0, wound.x + ww * 3, 0)
      // 釣鐘の形にぼかす (中心の細い白を作らない = 線に見せない)
      g.addColorStop(0, 'rgba(198,216,244,0)')
      g.addColorStop(0.15, 'rgba(198,216,244,0.08)')
      g.addColorStop(0.35, 'rgba(210,226,248,0.45)')
      g.addColorStop(0.5, 'rgba(232,240,255,0.78)')
      g.addColorStop(0.65, 'rgba(210,226,248,0.45)')
      g.addColorStop(0.85, 'rgba(198,216,244,0.08)')
      g.addColorStop(1, 'rgba(198,216,244,0)')
      ctx.globalAlpha = pil.level * profile.pillarAlpha * along
      ctx.fillStyle = g
      ctx.fillRect(wound.x - ww * 3, y1, ww * 6, y0 - y1)
    }
    // 柱の中を昇る光の粒
    const tt = (elapsedMs - profile.pullAtMs) / 1000
    for (let i = 0; i < 22; i++) {
      const sp = 260 + h01(i * 3.7) * 420
      const born = h01(i * 1.3) * 0.5
      const lt = tt - born
      if (lt < 0) continue
      const y = wound.y - lt * sp
      if (y < top || y < wound.y - height) continue
      const x = wound.x + (h01(i * 5.1) - 0.5) * w * 2.4
      glow(ctx, x, y, 2 + h01(i * 2.2) * 2, SOUL_BLUE.white, pil.level * 0.8 * (1 - Math.min(1, lt / 1.2)))
    }
    // 根元の照り返し (床)
    glow(ctx, wound.x, floorY, 60 * pil.rise, SOUL_BLUE.haze, pil.level * 0.25)
  }
  ctx.restore()
  // 抜いた瞬間の飛沫 (柱より手前)
  const tp = (elapsedMs - profile.pullAtMs) / 1000
  if (tp >= 0) drawPullSpray(ctx, wound.x, wound.y, away, tp + 0.02, profile.sprayBlue, profile.sprayEmber, 71, 1.15)
}
